import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { ClassNames } from '../../../constants/class-names.constants';
import { LsqBaseColumnComponent } from './base-column.component';
import { Icons } from '../../../core';
import { Table } from '../../../common';

@Component({
    selector: 'lsq-percent-column',
    template: `
        <ng-container [cdkColumnDef]="column.key">
            <th
                cdk-header-cell
                *cdkHeaderCellDef
                [ngStyle]="{ width: column.width }"
                [ngClass]="[
                    classNames.AlignRight,
                    column.sortable !== false ? classNames.Sortable : 'not-sortable',
                    !!column.headerPopoverContent ? classNames.ThWithPopover : '',
                    column.className || ''
                ]"
                (click)="changeSort()"
            >
                <div class="th-content">
                    <ng-container *ngIf="column.headerPopoverContent && !column.popoverIcon">
                        <popover-content
                            #headerPopover
                            [size]="column.headerPopoverSize || 'small'"
                        >
                            {{ column.headerPopoverContent }}
                        </popover-content>
                        <div
                            [popover]="headerPopover"
                            [popoverPlacement]="getHeaderPopoverPlacement()"
                        >
                            <span [innerHtml]="column.header"></span>
                        </div>
                    </ng-container>
                    <ng-container *ngIf="column.headerPopoverContent && column.popoverIcon">
                        <span [innerHtml]="column.header"></span>
                        <lsq-icon
                            [type]="icons.QuestionCircleLight"
                            [popoverOnHover]="true"
                            [height]="14"
                            [width]="14"
                            [style.marginLeft]="'1rem'"
                            [popover]="column.headerPopoverContent"
                        >
                        </lsq-icon>
                    </ng-container>
                    <ng-container *ngIf="!column.headerPopoverContent">
                        <span [innerHtml]="column.header"></span>
                    </ng-container>

                    <div *ngIf="isSortable$ | async">
                        <lsq-icon
                            class="sort-caret"
                            [ngClass]="{ 'is-sorted': isCurrentlySorted() }"
                            [type]="getIconType()"
                            [color]="getIconColor()"
                        >
                        </lsq-icon>
                    </div>
                </div>
            </th>

            <td
                cdk-cell
                *cdkCellDef="let row"
                [ngStyle]="{ width: column.width }"
                [ngClass]="[
                    classNames.AlignRight,
                    column.sortable !== false ? classNames.Sortable : 'not-sortable',
                    column.className || ''
                ]"
            >
                <ng-container *ngIf="!!row[column.key]; else nullDash">
                    <ng-container *ngIf="isPercentWhole; else fromDecimal">
                        {{ row[column.key] | lsqPercent: '1.0' }}
                    </ng-container>
                    <ng-template #fromDecimal>
                        <ng-container *ngIf="isPercentFromDecimal; else customDigitInfo">
                            {{ row[column.key] | lsqPercent: (percentDigitInfo ?? '1.0-1'):true }}
                        </ng-container>
                    </ng-template>
                    <ng-template #customDigitInfo>
                        <ng-container *ngIf="percentDigitInfo; else default">
                            {{ row[column.key] | lsqPercent: percentDigitInfo:false }}
                        </ng-container>
                    </ng-template>

                    <ng-template #default>
                        {{ row[column.key] | lsqPercent }}
                    </ng-template>
                </ng-container>
                <ng-template #nullDash>
                    {{ nullDisplay }}
                </ng-template>
            </td>

            <td
                cdk-footer-cell
                *cdkFooterCellDef
                [ngClass]="[
                    classNames.AlignRight,
                    column.sortable !== false ? classNames.Sortable : 'not-sortable'
                ]"
            >
                <!-- Custom Column Cell Footer -->
                <div *ngFor="let customFooter of customColumnFooters">
                    <ng-container
                        *ngTemplateOutlet="customFooter.templateRef; context: { row: row }"
                    ></ng-container>
                </div>

                <ng-container *ngIf="!customColumnFooters.length">
                    <ng-container *ngIf="totalValue">
                        <ng-container *ngIf="isPercentWhole; else fromDecimalTotal">
                            {{ totalValue | lsqPercent: '1.0' }}
                        </ng-container>
                        <ng-template #fromDecimalTotal>
                            <ng-container *ngIf="isPercentFromDecimal; else fromDecimalWholeTotal">
                                {{ totalValue | lsqPercent: '1.0-1':true }}
                            </ng-container>
                        </ng-template>
                        <ng-template #fromDecimalWholeTotal>
                            <ng-container
                                *ngIf="isPercentFromDecimalWhole; else customDigitInfoTotal"
                            >
                                {{ totalValue | lsqPercent: '1.0':true }}
                            </ng-container>
                        </ng-template>
                        <ng-template #customDigitInfoTotal>
                            <ng-container *ngIf="percentDigitInfo; else defaultTotal">
                                {{ totalValue | lsqPercent: percentDigitInfo:true }}
                            </ng-container>
                        </ng-template>
                        <ng-template #defaultTotal>
                            {{ totalValue | lsqPercent }}
                        </ng-template>
                    </ng-container>
                </ng-container>
            </td>
        </ng-container>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LsqPercentColumnComponent extends LsqBaseColumnComponent implements OnInit {
    @Input() public totalValue: number;
    /** Should be true if the data will be between 0 and 100, and should be displayed as a whole number percentage. */
    @Input() public isPercentWhole = false;
    /** Should be true if the data will be between 0 and 1. */
    @Input() public isPercentFromDecimal = false;
    /** Should be true if the data will be between 0 and 1, and should be displayed as a whole number percentage. */
    @Input() public isPercentFromDecimalWhole = false;
    /** This follows the format of PercentPipe */
    @Input() public percentDigitInfo?: string;
    /** Show (or not) empty string instead of dashes for null values */
    @Input() public nullDisplay = Table.NullAmount;

    public readonly classNames = ClassNames;
    public readonly icons = Icons;
}
